import React, { Dispatch, SetStateAction } from 'react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import {
  FilterState,
  PreScreenQuestion,
} from '../../../../../../utils/types/assessment_types';

type FilterScreenProps = {
  questions: PreScreenQuestion[];
  filters: FilterState[];
  setFilters: Dispatch<SetStateAction<FilterState[]>>;
};

export const FilterPreScreen: React.FC<FilterScreenProps> = ({
  questions,
  setFilters,
  filters,
}) => {
  const handleNumericChange = (
    questionId: string,
    field: 'min' | 'max',
    value: number,
  ) => {
    setFilters((prevFilters) => {
      const existingFilter = prevFilters.find(
        (filter) => filter.questionId === questionId,
      );

      if (existingFilter) {
        return prevFilters.map((filter) =>
          filter.questionId === questionId
            ? {
                ...filter,
                filterNumber: { ...filter.filterNumber, [field]: value },
              }
            : filter,
        );
      }

      return [
        ...prevFilters,
        {
          questionId,
          questionType: 'numeric',
          filterNumber: { [field]: value },
        },
      ];
    });
  };

  const handleChoiceChange = (questionId: string, choice: string) => {
    setFilters((prevFilters) => {
      const existingFilter = prevFilters.find(
        (filter) => filter.questionId === questionId,
      );

      if (existingFilter) {
        const updatedFilterChoices = existingFilter.filterChoices?.includes(
          choice,
        )
          ? existingFilter.filterChoices.filter((c) => c !== choice)
          : [...(existingFilter.filterChoices || []), choice];

        if (updatedFilterChoices.length === 0) {
          return prevFilters.filter(
            (filter) => filter.questionId !== questionId,
          );
        }

        return prevFilters.map((filter) =>
          filter.questionId === questionId
            ? {
                ...filter,
                filterChoices: updatedFilterChoices,
              }
            : filter,
        );
      }

      return [
        ...prevFilters,
        {
          questionId,
          questionType: 'multiple-choice',
          filterChoices: [choice],
        },
      ];
    });
  };

  return (
    <Stack spacing={2}>
      {questions.map((question) => (
        <Paper
          key={question.questionId}
          variant="outlined"
          sx={{ p: 2, borderRadius: 2 }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {question.question}
          </Typography>

          {question.questionType === 'numeric' && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Min"
                  type="number"
                  onChange={(e) =>
                    handleNumericChange(
                      question.questionId,
                      'min',
                      Number(e.target.value),
                    )
                  }
                  value={
                    filters.find(
                      (option) => option.questionId === question.questionId,
                    )?.filterNumber?.min ?? ''
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Max"
                  type="number"
                  onChange={(e) =>
                    handleNumericChange(
                      question.questionId,
                      'max',
                      Number(e.target.value),
                    )
                  }
                  value={
                    filters.find(
                      (option) => option.questionId === question.questionId,
                    )?.filterNumber?.max ?? ''
                  }
                />
              </Grid>
            </Grid>
          )}

          {question.questionType === 'multiple-choice' && (
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {question.choices?.map((choice) => (
                <FormControlLabel
                  key={choice}
                  control={
                    <Checkbox
                      size="small"
                      checked={
                        filters
                          .find(
                            (option) =>
                              option.questionId === question.questionId,
                          )
                          ?.filterChoices?.includes(choice) || false
                      }
                      onChange={() =>
                        handleChoiceChange(question.questionId, choice)
                      }
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      {choice}
                    </Typography>
                  }
                />
              ))}
            </Stack>
          )}
        </Paper>
      ))}
    </Stack>
  );
};
